import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  PlayCircleFilledOutlined,
  PauseCircleFilledOutlined,
  ShareOutlined,
  VolumeUpOutlined,
  VolumeOffOutlined,
  FileDownloadOutlined,
} from '@mui/icons-material';
import QoreTalksShell, { getCategoryStyle } from './QoreTalksShell';
import EpisodeCard from './EpisodeCard';
import QtEmptyState from './QtEmptyState';
import { fetchTalkById, fetchTalks } from '../../services/qoreTalksApi';
import { hostDisplayName } from '../../constants/qoreTalks';
import {
  saveListeningProgress,
  getListeningProgress,
  getRelatedTalks,
} from './qoreTalksUtils';

const fmtTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const TalkDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const audioRef = useRef(null);

  const [talk, setTalk] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchTalkById(id);
        if (!cancelled) {
          setTalk(data);
          const all = await fetchTalks({ limit: 30 }).catch(() => []);
          if (!cancelled) setRelated(getRelatedTalks(all, id, 3));
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => {
      setCurrentTime(el.currentTime);
      if (el.duration && !isNaN(el.duration)) {
        saveListeningProgress(id, el.currentTime, el.duration);
      }
    };
    const onDur = () => {
      setDuration(el.duration);
      const saved = getListeningProgress();
      if (saved?.episodeId === id && saved.currentTime > 5) {
        el.currentTime = saved.currentTime;
        setCurrentTime(saved.currentTime);
      }
    };
    const onEnd = () => setPlaying(false);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onDur);
    el.addEventListener('ended', onEnd);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onDur);
      el.removeEventListener('ended', onEnd);
    };
  }, [talk, id]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      el.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: talk?.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t('qoreTalks.linkCopied'));
      }
    } catch {
      /* user cancelled share */
    }
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el) return;
    const pct = Number(e.target.value);
    el.currentTime = (pct / 100) * (duration || 0);
  };

  if (loading) {
    return (
      <QoreTalksShell activeTab="home" mainClass="qt-main--narrow">
        <div className="qt-loading" style={{ minHeight: 320 }}>
          <div className="qt-spinner" aria-label="Loading" />
        </div>
      </QoreTalksShell>
    );
  }

  if (error || !talk) {
    return (
      <QoreTalksShell activeTab="home" mainClass="qt-main--narrow">
        <QtEmptyState
          variant="episodes"
          actions={
            <Link to="/qoretalks" className="qt-btn qt-btn--primary">
              {t('notFound.backHome')}
            </Link>
          }
        />
      </QoreTalksShell>
    );
  }

  const catStyle = getCategoryStyle(talk.category);
  const audioSrc = talk.audioUrl;
  const videoSrc = talk.videoUrl;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const hasThumb = talk.thumbnailUrl && !talk.thumbnailUrl.includes('/api/placeholder');

  return (
    <QoreTalksShell activeTab="home" mainClass="qt-main--narrow">
      <nav className="qt-breadcrumb" aria-label="Breadcrumb">
        <Link to="/qoretalks">{t('qoreTalks.pageTitle')}</Link>
        {' / '}
        <span>{talk.title}</span>
      </nav>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        <span className="qt-card__category" style={catStyle}>
          {talk.category}
        </span>
        {talk.season?.episode && (
          <span className="qt-tag">
            S{talk.season.number || 1} E{talk.season.episode}
          </span>
        )}
      </div>

      <h1 style={{ fontSize: 'clamp(1.5rem, 4vw, 2.1rem)', fontWeight: 900, lineHeight: 1.2, margin: '0 0 1rem' }}>
        {talk.title}
      </h1>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', color: '#94a3b8', fontSize: '0.875rem' }}>
        <span className="qt-card__host-avatar" style={{ ...catStyle, width: 36, height: 36, fontSize: '0.85rem' }}>
          {hostDisplayName(talk.host).charAt(0)}
        </span>
        <span>{hostDisplayName(talk.host)}</span>
        {talk.duration ? <span>· {talk.duration} min</span> : null}
      </div>

      {videoSrc && <video className="qt-detail-player" controls src={videoSrc} />}

      {(audioSrc || hasThumb) && !videoSrc && (
        <div className="qt-player-card" style={catStyle}>
          <div className="qt-player-card__art">
            {hasThumb ? (
              <img src={talk.thumbnailUrl} alt="" loading="lazy" />
            ) : (
              <PlayCircleFilledOutlined sx={{ fontSize: 64, color: catStyle['--qt-cat-accent'], opacity: 0.5 }} />
            )}
          </div>
          {audioSrc && (
            <div className="qt-player-card__body">
              <audio ref={audioRef} src={audioSrc} preload="metadata" />
              <div className="qt-player-card__controls">
                <button type="button" className="qt-btn qt-btn--primary" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
                  {playing ? <PauseCircleFilledOutlined /> : <PlayCircleFilledOutlined />}
                </button>
                <span style={{ fontSize: '0.75rem', color: '#64748b', minWidth: 36 }}>{fmtTime(currentTime)}</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={progress}
                  onChange={seek}
                  className="qt-player-card__scrub"
                  aria-label="Seek"
                />
                <span style={{ fontSize: '0.75rem', color: '#64748b', minWidth: 36 }}>{fmtTime(duration)}</span>
                <button type="button" className="qt-btn qt-btn--ghost" onClick={() => setMuted((p) => !p)} aria-label={muted ? 'Unmute' : 'Mute'}>
                  {muted ? <VolumeOffOutlined /> : <VolumeUpOutlined />}
                </button>
                <button type="button" className="qt-btn qt-btn--ghost" onClick={handleShare} aria-label={t('qoreTalks.share')}>
                  <ShareOutlined />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!audioSrc && !videoSrc && (
        <button type="button" className="qt-btn qt-btn--ghost" onClick={handleShare}>
          <ShareOutlined sx={{ mr: 0.5 }} /> {t('qoreTalks.share')}
        </button>
      )}

      {talk.description && (
        <p style={{ color: '#cbd5e1', lineHeight: 1.7, margin: '1.25rem 0' }}>{talk.description}</p>
      )}

      {talk.showNotes && (
        <div className="qt-detail-notes">
          <h4>{t('qoreTalks.showNotes')}</h4>
          <p style={{ margin: 0, color: '#94a3b8', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{talk.showNotes}</p>
        </div>
      )}

      {talk.transcriptUrl && (
        <div className="qt-detail-notes">
          <h4>{t('qoreTalks.transcript')}</h4>
          <a
            href={talk.transcriptUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="qt-btn qt-btn--ghost"
          >
            <FileDownloadOutlined sx={{ fontSize: 18 }} /> {t('qoreTalks.downloadTranscript')}
          </a>
        </div>
      )}

      {talk.resources?.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: '#94a3b8' }}>{t('qoreTalks.resources')}</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {talk.resources.map((r, i) => (
              <a
                key={i}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="qt-btn qt-btn--ghost"
                style={{ justifyContent: 'flex-start' }}
              >
                {r.title || r.url}
              </a>
            ))}
          </div>
        </div>
      )}

      {related.length > 0 && (
        <section className="qt-related">
          <h2>{t('qoreTalks.relatedEpisodes')}</h2>
          <div className="qt-grid">
            {related.map((rt) => (
              <EpisodeCard
                key={rt._id || rt.id}
                talk={rt}
                onPlay={(ep) => navigate('/qoretalks/episodes/' + (ep._id || ep.id))}
                onLike={() => {}}
                liked={false}
              />
            ))}
          </div>
        </section>
      )}
    </QoreTalksShell>
  );
};

export default TalkDetail;